/**
 * GiftCardsCard — one gift card as a phone/tablet card (BON-223).
 *
 * The six table columns become a two-line card: card number and balance
 * lead, the customer sits under them, and status/source/date share the
 * footer line — the same fields in the same reading order as the table.
 *
 * The card IS the link, mirroring `ReviewQueueCard` (BON-381): a
 * `role="button"` div wrapping a real link is an ARIA violation (a widget
 * role must not contain another interactive element), and one `<Link>` gets
 * a real accessible role, open-in-new-tab and long-press for free.
 */
import { Link } from 'react-router-dom';

import { formatMoney } from '../review/reviewQueueModel';

import { formatCreatedDate, cardNumberLabel, sourceLabel } from './giftCardsModel';
import { GiftCardsTableCustomerCell } from './GiftCardsTableCustomerCell';
import { GiftCardStatusPill } from './GiftCardStatusPill';
import type { GiftCardListRow } from './giftCardTypes';

const CARD_STYLE = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  color: 'inherit',
  textDecoration: 'none',
  background: 'var(--nb-bg)',
  border: '1px solid var(--nb-border)',
  borderRadius: 12,
  padding: '12px 13px',
} as const;

const FOOTER_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 9,
  paddingTop: 9,
  borderTop: '1px solid var(--nb-border)',
} as const;

export function GiftCardsCard({ row }: { row: GiftCardListRow }) {
  return (
    <Link
      to={`/admin/gift-cards/${row.id}`}
      aria-label={`Open gift card ${cardNumberLabel(row)}`}
      style={CARD_STYLE}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span
          className="nb-mono"
          style={{ fontSize: 14, letterSpacing: '.04em', color: 'var(--nb-ink-2)' }}
        >
          {cardNumberLabel(row)}
        </span>
        <span style={{ flex: 1 }} />
        <span
          className="nb-tabular"
          style={{
            fontSize: 15,
            fontWeight: 800,
            color: row.balanceCents === 0 ? 'var(--nb-faint)' : 'var(--nb-ink)',
          }}
        >
          {formatMoney(row.balanceCents, row.currency)}
        </span>
      </div>

      <GiftCardsTableCustomerCell customer={row.customer} />

      <div style={FOOTER_STYLE}>
        <GiftCardStatusPill status={row.status} />
        <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--nb-muted)' }}>
          {sourceLabel(row.source)}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 10.5, color: 'var(--nb-subtle)' }}>
          {formatCreatedDate(row.createdAt)}
        </span>
      </div>
    </Link>
  );
}
